import React, { useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import { getDeepCopyOfObject } from 'shared/utils/get-deep-copy-of-object'
import { useCourseData } from 'modules/course/hooks/use-course-data'
import { useCourseMenu } from 'modules/course/hooks/use-course-menu'
import { useSendCourseQuizAnswers } from 'modules/course/hooks/use-send-course-quiz-answers'
import useStepRouter from 'modules/course/hooks/use-step-router'
import type { CourseQuizDataInProgressInterface } from 'modules/course/types/course-quiz-interface'
import { addPublicCustomClassNamePrefix } from 'modules/course/utils/add-class-prefix'
import { SecondaryButtonWithCustomTheme } from '../../components/secondary-button-with-custom-theme'
import { CourseQuizLayout } from '../course-quiz-layout'
import { CourseQuizStateContext } from '../course-quiz-state/context'
import { CourseStatusEnum } from '../course-quiz-state/types'
import { QuestionItem } from '../question-item'
import { AnswersList } from './answers-list'

interface CourseQuizInProgressScreenProps {
  className?: string
  courseQuizData: CourseQuizDataInProgressInterface | undefined
}

export const CourseQuizInProgressScreen = ({
  className,
  courseQuizData,
}: React.PropsWithChildren<CourseQuizInProgressScreenProps>): ReactNode => {
  const { t } = useTranslation()

  const [isFetching, setIsFetching] = useState(false)
  const [selectedAnswers, setSelectedAnswers] = useState<Array<Array<boolean>> | undefined>(
    undefined,
  )
  const { step } = useStepRouter()
  const { setQuizState } = useContext(CourseQuizStateContext)

  const { courseData, mutate: mutateCourseDate, isValidating } = useCourseData()
  const { mutate: mutateCourseMenu } = useCourseMenu({
    courseId: courseData?.id,
  })
  const { sendCourseQuizAnswers } = useSendCourseQuizAnswers()

  const onSendCourseQuizAnswers = async (
    ...args: Parameters<ReturnType<typeof useSendCourseQuizAnswers>['sendCourseQuizAnswers']>
  ): Promise<void> => {
    try {
      const courseQuizId = args[0].courseQuizId
      setIsFetching(true)
      const { passed, percentage } = await sendCourseQuizAnswers(...args)

      if (passed) {
        await mutateCourseMenu(
          data =>
            data?.map(module => ({
              ...module,
              quiz:
                module.quiz?.id === courseQuizId
                  ? { ...module.quiz, completed: true }
                  : module.quiz,
            })),
          false,
        )
        await mutateCourseDate()
      }
      setIsFetching(false)
      setQuizState({
        status: passed ? CourseStatusEnum.pass : CourseStatusEnum.failed,
        percentage,
      })
      window.scrollTo({ top: 0 })
    } catch (error) {
      setIsFetching(false)
      throw error
    }
  }

  const allAnswersAnswered = useMemo(
    () => selectedAnswers?.every(question => question.some(Boolean)),
    [selectedAnswers],
  )

  useEffect(() => {
    if (courseQuizData) {
      setSelectedAnswers(courseQuizData.q.map(({ a }) => a.map(() => false)))
    }
  }, [courseQuizData])

  return (
    <CourseQuizLayout
      className={`${className} ${addPublicCustomClassNamePrefix('course-quiz__container--in-progress')}`}
      questionCounter={t(
        (courseQuizData?.q.length || 0) > 1
          ? 'course.quiz.questions_counter_other'
          : 'course.quiz.questions_counter_one',
        { number: courseQuizData?.q.length },
      )}
      questions={courseQuizData?.q.map(({ t: question, a: answers }, index) => (
        <QuestionItem key={index} question={question}>
          <AnswersList
            answers={answers}
            selectedAnswers={selectedAnswers && selectedAnswers[index]}
            onClick={answerIndex =>
              setSelectedAnswers(prev => {
                if (!prev) return prev
                const copy = getDeepCopyOfObject(prev)
                copy[index][answerIndex] = !copy[index][answerIndex]
                return copy
              })
            }
          />
        </QuestionItem>
      ))}
      buttons={
        <SecondaryButtonWithCustomTheme
          className={`font-bold ${addPublicCustomClassNamePrefix('course__button--answer-quiz')}`}
          disabled={!allAnswersAnswered}
          isLoading={isFetching || isValidating}
          onClick={() =>
            step?.id &&
            onSendCourseQuizAnswers({
              courseQuizId: step.id,
              data: selectedAnswers,
            })
          }
        >
          {t('course.quiz.answer_button')}
        </SecondaryButtonWithCustomTheme>
      }
    />
  )
}
